import clsx from "clsx";
import React from "react";
import Highlighter from "react-highlight-words";
import { makeStyles } from "tss-react/mui";

/* eslint-disable no-restricted-imports */
import {
    Box,
    styled,
    Table,
    TableBody,
    TableCell,
    TableCellProps,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TooltipProps,
    Typography,
} from "@mui/material";
/* eslint-enable no-restricted-imports */
import { Theme, useTheme } from "@mui/material/styles";

import { CustomBadge } from "../CustomBadge";
import { panelHeaderMinHeight } from "../SectionPanel/SectionPanel";
import { Tooltip } from "../Tooltip";
import { useIsClipped } from "../useIsClipped";

import { useStyles as useTableHeaderStyles } from "./TableHeader.style";

export const tableRowHeight = 50;

export const MinimalTable = styled(Table)(() => ({
    borderCollapse: "collapse",
}));

/**
 * A row without hover styling: header rows, empty-state rows and body rows of tables that are not interactive.
 * Interactive body rows use `HoverRow`.
 */
export const MinimalTableRow = React.forwardRef<HTMLTableRowElement, React.ComponentProps<typeof TableRow>>((props, ref) => (
    <TableRow ref={ref} {...props} />
));

/**
 * A plain table body for rows that do not come from a TanStack table. A TanStack table uses `TableBody`
 * (`@shared/table/TableBody`), which also renders the loading, empty and error states.
 */
export const MinimalTableBody = React.forwardRef<HTMLTableSectionElement, React.ComponentProps<typeof TableBody>>(
    (props, ref) => <TableBody ref={ref} {...props} />,
);
const StyledMinimalTableHeader = styled(TableHead)(({ theme }) => ({
    position: "sticky",
    top: -0.5,
    zIndex: 2,
    "& .MuiTableRow-root[data-title-row]": {
        borderBottom: `1px solid ${theme.palette.emteria.border.secondary}`,
    },
}));

// This row owns the geometry of every titled table's heading (see .claude/conventions/ui.md). Adjust it here so
// all of them move together, rather than restyling a title row at a call site.
const useTitleRowStyles = makeStyles()((theme: Theme) => ({
    titleCell: {
        padding: theme.spacing(2, 3, 2, 3),
        backgroundColor: theme.palette.emteria.background.paper,
        border: "none",
    },
    titleContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        minHeight: panelHeaderMinHeight,
    },
    titleLabel: {
        minWidth: 0,
    },
    titleActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexShrink: 0,
    },
}));

type MinimalTableTitle = {
    label: string;
    colSpan: number;
    actions?: React.ReactNode;
};

type MinimalTableHeaderProps = Omit<React.ComponentProps<typeof TableHead>, "title"> & {
    title?: MinimalTableTitle;
};

/**
 * The table head. Passing `title` prepends a title row on the paper background, separated
 * from the tinted column headers by a single hairline, so the title belongs to the table it
 * heads instead of reading as page furniture above it.
 */

export const MinimalTableHeader = ({ title, children, ...props }: MinimalTableHeaderProps) => {
    const { classes } = useTitleRowStyles();
    const { ref: labelRef, clipped: isLabelClipped } = useIsClipped(title?.label);

    return (
        <StyledMinimalTableHeader {...props}>
            {title ? (
                <TableRow data-title-row="true">
                    <TableCell component="th" scope="colgroup" colSpan={title.colSpan} className={classes.titleCell}>
                        <Box className={classes.titleContent}>
                            <Tooltip placement="top" title={title.label} hide={!isLabelClipped} disableChildrenWrapper>
                                <Typography
                                    ref={labelRef}
                                    component="div"
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    noWrap
                                    className={classes.titleLabel}
                                >
                                    {title.label}
                                </Typography>
                            </Tooltip>
                            {title.actions ? <Box className={classes.titleActions}>{title.actions}</Box> : null}
                        </Box>
                    </TableCell>
                </TableRow>
            ) : null}
            {children}
        </StyledMinimalTableHeader>
    );
};

const useContainerStyles = makeStyles()((theme: Theme) => ({
    container: {
        border: `1px solid ${theme.palette.emteria.border.primary}`,
        borderRadius: theme.spacing(2),
        backgroundColor: theme.palette.emteria.background.paper,
    },
}));

type MinimalTableContainerProps = React.ComponentProps<typeof TableContainer> & {
    /**
     * Drops the border, radius and background, for a table whose parent already provides that surface
     * (a `SectionPanel disablePadding`, a modal). A second surface inside the first would draw a double border.
     */
    plain?: boolean;
};

/**
 * The surface a table sits on. Same border, radius and background as `SectionPanel`, so a table with a
 * title row and one without read as the same component. `TableContainer` keeps its own `overflow-x: auto`,
 * which is what clips the sticky header to the rounded corners.
 */
export const MinimalTableContainer = React.forwardRef<HTMLDivElement, MinimalTableContainerProps>(
    ({ className, plain, ...props }, ref) => {
        const { classes, cx } = useContainerStyles();
        return <TableContainer ref={ref} className={cx(!plain && classes.container, className)} {...props} />;
    },
);

const useStyles = makeStyles()((theme: Theme) => ({
    tableCell: {
        height: tableRowHeight,
        fontSize: 16,
        padding: theme.spacing(1, 3),
        borderTop: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        border: "none",
    },
    fluidCell: {
        width: "100%",
        maxWidth: 0,
    },
}));

type MinimalTableCellProps = TableCellProps & {
    fluid?: boolean;
    fluidMinWidth?: React.CSSProperties["minWidth"];
};

export const MinimalTableCell = ({ children, fluid, fluidMinWidth, ...props }: MinimalTableCellProps) => {
    const { classes } = useStyles();
    return (
        <TableCell
            {...props}
            className={clsx(classes.tableCell, fluid && classes.fluidCell, props.className)}
            style={fluid && fluidMinWidth ? { minWidth: fluidMinWidth, ...props.style } : props.style}
        >
            {children}
        </TableCell>
    );
};

export const MinimalTableExpandCell = ({ style, ...props }: TableCellProps) => (
    <MinimalTableCell {...props} style={{ width: 1, ...style }} />
);

interface CellWithTooltipProps {
    title: string;
    displayValue?: string;
    testid?: string;
    style?: React.CSSProperties;
    tooltipProps?: Omit<TooltipProps, "children" | "title">;
    highlight?: string;
}

type TruncatedTextProps = Omit<CellWithTooltipProps, "style"> & {
    maxWidth?: React.CSSProperties["maxWidth"];
};

export const TruncatedText = ({ title, displayValue, testid, maxWidth, tooltipProps, highlight }: TruncatedTextProps) => {
    const theme = useTheme();
    const resolvedMaxWidth = typeof maxWidth === "number" ? `${maxWidth}px` : (maxWidth ?? "100%");
    const text = displayValue ?? title;
    const { ref: contentRef, clipped } = useIsClipped(`${text}|${resolvedMaxWidth}`);

    return (
        <Tooltip placement="top" title={title} hide={!clipped} disableChildrenWrapper {...tooltipProps}>
            <Typography
                ref={contentRef}
                component="span"
                noWrap
                data-testid={testid}
                style={{ maxWidth: resolvedMaxWidth, display: "block", minWidth: 0 }}
            >
                {highlight ? (
                    <Highlighter
                        searchWords={[highlight]}
                        textToHighlight={text}
                        autoEscape
                        unhighlightStyle={{ color: theme.palette.emteria.text.primary }}
                    />
                ) : (
                    text
                )}
            </Typography>
        </Tooltip>
    );
};

export const MinimalTableCellWithTooltip = ({ style, ...rest }: CellWithTooltipProps) => {
    const maxWidth = typeof style?.maxWidth === "number" ? `${style.maxWidth}px` : (style?.maxWidth ?? "200px");

    return (
        <MinimalTableCell style={{ ...style, overflow: "hidden", textOverflow: "clip", maxWidth }}>
            <TruncatedText {...rest} maxWidth={maxWidth} />
        </MinimalTableCell>
    );
};

const StyledMinimalHeaderCell = styled((props: MinimalTableHeaderCellProps) => <TableCell {...props} />)(({ theme }) => ({
    height: tableRowHeight,
    padding: theme.spacing(0, 3, 0, 3),
    borderBottom: "none",
    fontSize: 16,
    fontWeight: 600,
    backgroundColor: theme.palette.emteria.background.tableHeaders,
    color: `${theme.palette.emteria.text.primary} !important`,
    whiteSpace: "nowrap",
    "& .MuiTableSortLabel-root": {
        color: `${theme.palette.emteria.text.primary} !important`,
        fontWeight: 600,
        "&:hover": {
            color: `${theme.palette.emteria.text.primary} !important`,
        },
        "&.MuiTableSortLabel-active": {
            color: `${theme.palette.emteria.text.primary} !important`,
        },
        "& .MuiTableSortLabel-icon": {
            color: "inherit !important",
        },
    },
}));

type Item = {
    id: string;
    label: string;
};
interface MinimalTableHeaderCellProps extends TableCellProps {
    item?: Item;
    sort?: { orderBy: string; orderDirection: "asc" | "desc" };
    activeFiltersCount?: number;
    onSort?: (orderBy: string) => void;
    onFilter?: (item: Item) => void;
    sortableColumnIds?: string[];
    filterableColumnIds?: string[];
    align?: "inherit" | "left" | "center" | "right" | "justify";
    sortable?: boolean;
    sortDirection?: "asc" | "desc" | false;
    cellProps?: TableCellProps;
}
export const MinimalTableHeaderCell = (props: MinimalTableHeaderCellProps) => {
    const { classes } = useTableHeaderStyles();
    const { item, sort, activeFiltersCount, sortableColumnIds, filterableColumnIds, children, ...rest } = props;

    if (!item) {
        return <StyledMinimalHeaderCell {...rest}>{children}</StyledMinimalHeaderCell>;
    }

    const isSortable = sortableColumnIds?.includes(item.id);
    const isFilterable = filterableColumnIds?.includes(item.id);

    const onSort = () => {
        if (props.onSort) {
            props.onSort(item.id);
        }
    };

    const onFilter = () => {
        if (props.onFilter) {
            props.onFilter(item);
        }
    };

    if (isSortable) {
        return (
            <StyledMinimalHeaderCell align="left" sortDirection={sort?.orderDirection} {...props.cellProps}>
                <TableSortLabel
                    active={sort?.orderBy === item.id}
                    direction={sort?.orderDirection || "asc"}
                    className={classes.sortLabel}
                    onClick={onSort}
                >
                    <Typography>{item.label}</Typography>
                </TableSortLabel>
            </StyledMinimalHeaderCell>
        );
    }

    if (isFilterable) {
        return (
            <StyledMinimalHeaderCell {...props.cellProps}>
                <Box display="flex" alignItems="center" gap={3}>
                    <Typography component="span" fontSize={16} fontWeight={600} lineHeight={1.2}>
                        {item.label}
                    </Typography>

                    <CustomBadge
                        className={clsx(classes.badge, classes.withPointer)}
                        badgeContent={activeFiltersCount}
                        onClick={onFilter}
                        data-testid="tag-filter-button"
                        overlap="rectangular"
                    >
                        <i className="fa-solid fa-filter" />
                    </CustomBadge>
                </Box>
            </StyledMinimalHeaderCell>
        );
    }

    return (
        <StyledMinimalHeaderCell align="left" {...props.cellProps}>
            <Typography>{item.label}</Typography>
        </StyledMinimalHeaderCell>
    );
};
